import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Tag, Button, Steps, Collapse, Alert, Modal, Tabs } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  VideoCameraOutlined,
  CheckCircleFilled,
  LaptopOutlined,
  PhoneOutlined,
  LockOutlined,
  ReadOutlined,
  WindowsOutlined,
  AppleOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  ShoppingCartOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { toast } from '../../../utils/toast.js';
import { getApiBaseUrl } from '../../../api/client.js';
import { VIDEO_BATCH_TOOL } from '../data/videoBatchToolContent.js';
import { useToolAccess } from '../hooks/useToolAccess.js';
import { TOOL_PAYMENT_ENABLED } from '../../../constants/payment.js';
import '../styles/pharmacy-tool.css';

const GREEN = '#005537';
const formatVnd = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

/**
 * Video mẫu YouTube nhúng (đủ thanh điều khiển để khách tua, chỉnh âm lượng).
 * rel=0: hết video chỉ gợi ý video cùng kênh. vq=hd720 chỉ là đề nghị, YouTube tự chọn chất lượng theo mạng.
 */
function SampleVideo({ youtubeId, title }) {
  const params = new URLSearchParams({ rel: '0', playsinline: '1', vq: 'hd720' });
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', border: '1px solid #e2e8f0' }}>
      <iframe
        title={title}
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`}
        loading="lazy"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ width: '100%', aspectRatio: '9/16', display: 'block', border: 0 }}
      />
    </div>
  );
}

const sectionTitle = (text, sub, marginBottom = 18) => (
  <div style={{ marginBottom }}>
    <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>{text}</h2>
    {sub && <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>{sub}</p>}
  </div>
);

// Trang giới thiệu + hướng dẫn cài công cụ cài trên máy (file zip đặt trên máy chủ). Mặc định là công cụ tạo
// video hàng loạt; công cụ khác truyền `tool` (file nội dung trong data/) và `getDownload` (API cấp link tải).
// Tải về: bắt buộc đăng nhập; API cấp link tạm 5 phút rồi trình duyệt tải thẳng file từ máy chủ.
// Công cụ trả phí (gói TOOL_...) mà chưa mua: nút tải đổi thành nút mua, trang và video mẫu vẫn xem được.
// `getGuideDownload` (tùy chọn): thêm nút tải file hướng dẫn (PDF, hoặc định dạng ghi ở T.guideDownloadLabel), cùng quyền với file chính.
// `T.guidePath` (tùy chọn): bấm tải file chính thì mở thêm trang hướng dẫn ở tab mới.
// `T.externalDownload` (tùy chọn): file để ngoài máy chủ (Google Drive) — API trả { url, external: true }, mở link ở tab mới.
export function VideoBatchToolPage({
  tool: T = VIDEO_BATCH_TOOL,
  getDownload = pharmacyToolApi.getVideoToolDownload,
  getGuideDownload = null,
}) {
  const PAGE_PATH = `/tools/${T.slug}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [guideDownloading, setGuideDownloading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { access } = useToolAccess();
  const toolAccess = access?.[T.slug];
  const buyPlan = toolAccess?.requiredPlans?.find((p) => p.price > 0) || null;
  const mustBuy = !!user?.id && !!toolAccess && !toolAccess.hasAccess && !!buyPlan;

  const goBuy = () => {
    if (!TOOL_PAYMENT_ENABLED) {
      toast.info(`Nhắn Zalo ${T.support?.phoneLabel || ''} để được hỗ trợ mua công cụ.`);
      return;
    }
    navigate(`/checkout/${buyPlan.id}?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
  };

  const handleDownload = async (fetchLink = getDownload, setBusy = setDownloading) => {
    if (!user?.id) {
      setLoginModalOpen(true);
      return;
    }
    if (mustBuy) {
      goBuy();
      return;
    }
    // Mở tab hướng dẫn NGAY lúc bấm (trước await) để trình duyệt không chặn cửa sổ bật lên; lỗi thì đóng lại
    const guideTab = fetchLink === getDownload && T.guidePath ? window.open('', '_blank') : null;
    const externalTab = fetchLink === getDownload && T.externalDownload ? window.open('', '_blank') : null;
    setBusy(true);
    try {
      const data = await fetchLink();
      if (!data?.url) throw new Error('Chưa có link tải');
      if (data.external) {
        if (externalTab) externalTab.location.href = data.url;
        else window.open(data.url, '_blank', 'noopener');
        toast.success('Đã mở thư mục tải công cụ ở tab mới. Tải về rồi làm theo file hướng dẫn trong thư mục.');
        return;
      }
      // Link tạm do máy chủ cấp; máy chủ trả file kèm Content-Disposition nên trình duyệt tự tải về
      const a = document.createElement('a');
      a.href = `${getApiBaseUrl()}${data.url}`;
      a.download = data.fileName || '';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (guideTab) guideTab.location.href = T.guidePath;
      toast.success(
        guideTab
          ? 'Đang tải công cụ. Trang hướng dẫn đã mở ở tab mới.'
          : 'Đang tải công cụ. Tải xong hãy làm theo các bước cài đặt bên dưới.'
      );
    } catch (err) {
      guideTab?.close();
      externalTab?.close();
      toast.error(err.message || 'Chưa lấy được link tải, vui lòng thử lại sau');
    } finally {
      setBusy(false);
    }
  };

  const goLogin = () => {
    setLoginModalOpen(false);
    navigate('/login', { state: { from: { pathname: PAGE_PATH } } });
  };

  const scrollToInstall = () => {
    document.getElementById('huong-dan-cai-dat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const downloadButton = (size = 'large') => (
    <Button
      type="primary"
      size={size}
      icon={!user?.id ? <LockOutlined /> : mustBuy ? <ShoppingCartOutlined /> : <DownloadOutlined />}
      loading={downloading}
      onClick={() => handleDownload()}
      style={{ background: '#facc15', borderColor: '#facc15', color: '#0f172a', fontWeight: 800, borderRadius: 10 }}
    >
      {!user?.id
        ? T.loginLabel || 'Đăng nhập để tải công cụ'
        : mustBuy
          ? `Mua · ${formatVnd(buyPlan.price)} / ${buyPlan.durationMonths || 12} tháng`
          : T.downloadLabel || 'Tải công cụ (.zip)'}
    </Button>
  );

  // Nút tải file hướng dẫn PDF (chỉ khi có getGuideDownload); chưa mua thì ẩn vì nút chính đã là nút mua
  const guideButton = (size = 'large', ghost = true) =>
    getGuideDownload && user?.id && !mustBuy ? (
      <Button
        size={size}
        ghost={ghost}
        icon={T.guideDownloadLabel ? <FileWordOutlined /> : <FilePdfOutlined />}
        loading={guideDownloading}
        onClick={() => handleDownload(getGuideDownload, setGuideDownloading)}
        style={{ borderRadius: 10, fontWeight: 600 }}
      >
        {T.guideDownloadLabel || 'Tải hướng dẫn (PDF)'}
      </Button>
    ) : null;

  // Danh sách bước cài đặt (dùng cho cả tab Windows và macOS)
  const renderSteps = (list) => (
    <Steps
      className="video-tool-steps"
      direction="vertical"
      current={-1}
      items={list.map((s, idx) => ({
        // Vòng số và tiêu đề màu xanh thương hiệu thay cho màu xám mặc định của bước "chưa tới"
        icon: <span className="video-tool-step-num">{idx + 1}</span>,
        title: <span style={{ fontWeight: 700, color: GREEN }}>{`Bước ${idx + 1}: ${s.title}`}</span>,
        description: (
          <div style={{ paddingBottom: 8 }}>
            <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.6 }}>{s.desc}</div>
            {s.tip && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color: '#92400e',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: 8,
                  padding: '6px 10px',
                }}
              >
                Lưu ý: {s.tip}
              </div>
            )}
            {idx === 0 && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {downloadButton('middle')}
                {guideButton('middle', false)}
              </div>
            )}
          </div>
        ),
      }))}
    />
  );

  return (
    <div className="pharmacy-tool-wrapper">
      {/* Chưa phát hành: stage 'demo' hiện dải DEMO (màu xanh), còn lại hiện dải BẢN NHÁP */}
      {!T.published && (
        <Alert
          banner
          type={T.stage === 'demo' ? 'info' : 'warning'}
          message={
            isAdmin
              ? `${T.stage === 'demo' ? 'DEMO' : 'BẢN NHÁP'} — trang chưa phát hành (published: false trong ${T.contentFile || 'file nội dung công cụ'}), khách chưa thấy thẻ công cụ ở /tools.`
              : 'Công cụ đang trong giai đoạn chuẩn bị phát hành, nội dung có thể thay đổi.'
          }
        />
      )}

      {/* HERO */}
      <div className="tool-hero-banner video-tool-hero">
        <div className="main-container">
          <div style={{ marginBottom: 10 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ
            </Link>
          </div>
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} lg={13}>
              <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, marginBottom: 10 }}>
                <VideoCameraOutlined /> {T.kindLabel || 'CÔNG CỤ CÀI TRÊN MÁY TÍNH'}
              </Tag>
              <h1 className="tool-hero-title" style={{ fontSize: 30 }}>
                {T.name}
              </h1>
              <p className="tool-hero-subtitle" style={{ fontSize: 16 }}>
                {T.tagline}
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
                {downloadButton()}
                {guideButton()}
                {T.sampleVideos?.length > 0 && (
                  <Button
                    size="large"
                    ghost
                    icon={<PlayCircleOutlined />}
                    onClick={() => document.getElementById('video-mau')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    style={{ borderRadius: 10, fontWeight: 600 }}
                  >
                    Xem video mẫu
                  </Button>
                )}
                <Button
                  size="large"
                  ghost
                  icon={<ReadOutlined />}
                  onClick={scrollToInstall}
                  style={{ borderRadius: 10, fontWeight: 600 }}
                >
                  Xem hướng dẫn cài đặt
                </Button>
              </div>
              <div style={{ color: '#a7f3d0', fontSize: 12.5, marginTop: 10 }}>
                {T.platforms.some((x) => /Windows/i.test(x)) && <WindowsOutlined />}{' '}
                {[T.platforms.join(' · '), T.version && `Phiên bản v1`, T.fileSize].filter(Boolean).join(' · ')}
              </div>
            </Col>

            {/* Banner hình ảnh minh họa công cụ */}
            <Col xs={24} lg={11}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.28)',
                  border: '2px solid rgba(255, 255, 255, 0.22)',
                  aspectRatio: '2/1',
                  background: 'rgba(0, 0, 0, 0.15)',
                }}
              >
                <img
                  src={T.heroBanner || '/images/tools/tool-tao-video-hang-loat-hero.jpg'}
                  alt={T.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            </Col>
          </Row>

          {/* Dải thông số nổi bật */}
          {T.highlights?.length > 0 && (
            <div
              className="video-tool-highlights"
              style={{ '--video-tool-highlight-cols': T.highlights.length }}
            >
              {T.highlights.map((h) => (
                <div key={h.label} className="video-tool-highlight">
                  <span className="video-tool-highlight-value">{h.value}</span>
                  <span className="video-tool-highlight-label">{h.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="main-container" style={{ marginTop: 28, marginBottom: 40 }}>
        {/* GIỚI THIỆU + TÍNH NĂNG */}
        <section style={{ marginBottom: 36 }}>
          {sectionTitle('Công cụ làm được gì?', T.intro)}
          <Row gutter={[16, 16]}>
            {T.features.map((f) => (
              <Col xs={24} sm={12} lg={6} key={f.title}>
                <Card style={{ height: '100%', borderRadius: 14, border: '1px solid #e2e8f0' }}>
                  <CheckCircleFilled style={{ color: '#10b981', fontSize: 20 }} />
                  <h3 style={{ fontSize: 15.5, fontWeight: 700, margin: '10px 0 6px', color: '#0f172a' }}>{f.title}</h3>
                  <p style={{ color: '#475569', fontSize: 13.5, margin: 0, lineHeight: 1.55 }}>{f.desc}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* VIDEO MẪU XEM TRƯỚC (nếu có): YouTube Shorts nhúng, máy chủ không lưu video */}
        {T.sampleVideos?.length > 0 && (
          <section id="video-mau" style={{ marginBottom: 36, scrollMarginTop: 90 }}>
            {sectionTitle('Video mẫu làm bằng công cụ', T.sampleVideosNote || 'Xem trước video thật làm ra, trước khi quyết định mua.')}
            <Row gutter={[20, 20]} justify="center">
              {T.sampleVideos.map((v) => (
                <Col xs={24} sm={12} md={8} key={v.youtubeId}>
                  <SampleVideo youtubeId={v.youtubeId} title={v.title || `Video mẫu ${T.name}`} />
                  {v.title && <div style={{ marginTop: 8, fontWeight: 600, color: '#0f172a', textAlign: 'center' }}>{v.title}</div>}
                </Col>
              ))}
            </Row>
          </section>
        )}

        {/* VIDEO HƯỚNG DẪN (nếu có) */}
        {T.youtubeVideoId && (
          <section style={{ marginBottom: 36 }}>
            {sectionTitle('Xem nhanh cách dùng')}
            <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
              <iframe
                title={`Hướng dẫn ${T.name}`}
                src={`https://www.youtube-nocookie.com/embed/${T.youtubeVideoId}`}
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
          </section>
        )}

        <Row gutter={[24, 24]}>
          {/* HƯỚNG DẪN CÀI ĐẶT */}
          <Col xs={24} lg={15}>
            <section id="huong-dan-cai-dat" style={{ scrollMarginTop: 90 }}>
              {sectionTitle(T.installTitle || 'Hướng dẫn cài đặt', T.installSubtitle || 'Làm lần lượt từng bước, lần đầu mất khoảng 5–10 phút.')}
              <Card style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
                <Tabs
                  defaultActiveKey={T.installStepsMac?.length && /Mac/i.test(navigator.userAgent) ? 'mac' : 'windows'}
                  items={[
                    {
                      key: 'windows',
                      label: T.installTabLabel ? <span>{T.installTabLabel}</span> : <span><WindowsOutlined /> Windows</span>,
                      children: renderSteps(T.installSteps),
                    },
                    ...(T.installStepsMac?.length
                      ? [{ key: 'mac', label: <span><AppleOutlined /> macOS</span>, children: renderSteps(T.installStepsMac) }]
                      : []),
                  ]}
                />
              </Card>
            </section>
          </Col>

          {/* YÊU CẦU MÁY + HỖ TRỢ */}
          <Col xs={24} lg={9}>
            {/* 38px để thẻ bên phải thẳng hàng với thẻ hướng dẫn bên trái (tiêu đề bên trái có thêm dòng mô tả) */}
            {sectionTitle('Yêu cầu máy tính', null, 38)}
            <Card style={{ borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 20 }}>
              <div style={{ display: 'grid', gap: 10 }}>
                {T.requirements.map((r) => (
                  <div key={r.label} style={{ display: 'flex', gap: 10, fontSize: 14 }}>
                    <LaptopOutlined style={{ color: GREEN, marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.label}</div>
                      <div style={{ color: '#475569' }}>{r.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card style={{ borderRadius: 14, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div style={{ fontWeight: 800, color: GREEN, marginBottom: 6 }}>Cần hỗ trợ cài đặt?</div>
              <p style={{ color: '#334155', fontSize: 14, margin: '0 0 12px' }}>{T.support.text}</p>
              <Button icon={<PhoneOutlined />} href={`tel:${T.support.phone}`} style={{ borderRadius: 8 }}>
                Hotline / Zalo {T.support.phoneLabel}
              </Button>
              {T.updatedAt && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>Cập nhật lần cuối: {T.updatedAt}</div>
              )}
            </Card>
          </Col>
        </Row>

        {/* CÂU HỎI THƯỜNG GẶP */}
        {T.faqs?.length > 0 && (
          <section style={{ marginTop: 36 }}>
            {sectionTitle('Câu hỏi thường gặp')}
            <Collapse
              items={T.faqs.map((f, idx) => ({
                key: String(idx),
                label: <span style={{ fontWeight: 600 }}>{f.q}</span>,
                children: <p style={{ margin: 0, color: '#334155' }}>{f.a}</p>,
              }))}
            />
          </section>
        )}

        {/* KÊU GỌI CUỐI TRANG */}
        <div
          style={{
            marginTop: 36,
            background: `linear-gradient(135deg, ${GREEN} 0%, #047857 100%)`,
            borderRadius: 16,
            padding: '24px 28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h3 style={{ color: '#fff', fontSize: 20, fontWeight: 800, margin: 0 }}>{T.ctaTitle || 'Sẵn sàng làm video hàng loạt?'}</h3>
            <p style={{ color: '#d1fae5', margin: '4px 0 0' }}>
              {T.ctaText || 'Tải công cụ, cài trong vài phút và xuất video đầu tiên ngay hôm nay.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {downloadButton()}
            {guideButton()}
          </div>
        </div>
      </div>

      <Modal
        open={loginModalOpen}
        onCancel={() => setLoginModalOpen(false)}
        title="Đăng nhập để tải công cụ"
        okText="Đăng nhập"
        cancelText="Để sau"
        onOk={goLogin}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
      >
        <p>
          Công cụ dành cho tài khoản đã đăng ký trên daotaonhathuoc.com. Đăng nhập (hoặc{' '}
          <Link to="/register" onClick={() => setLoginModalOpen(false)}>
            đăng ký miễn phí
          </Link>
          ) rồi quay lại trang này để tải.
        </p>
      </Modal>
    </div>
  );
}
